import { api } from '@/shared/api/client'
import { qs, type QueryValue } from '@/shared/lib/query'
import type {
  AlertRuleResponse, AnalyticsRecomputeResult, BenfordResponse, BeneficiaryContactResponse,
  BeneficiaryEventResponse, BeneficiaryResponse, ConcentrationResponse, ControlCohortResponse,
  CrossTabResponse, CursorPage, DisbursementResponse, DocumentReserveResponse, DocumentResponse,
  DuplicateReportResponse, EcosystemResponse, EligibilityResponse, EquityResponse,
  ExportJobResponse, FieldSpec, FlaggedPage, FunnelResponse, GapsResponse, GeographyResponse,
  ImpactRefreshResponse, ImpactResponse, ImpactSummary, KpiResponse, MilestoneResponse,
  MoneyResponse, MultiProgrammePage, PartnerResponse, PortfolioOverviewResponse,
  PreviewResponse, ProgramCoveragePanelResponse, ProgramCoverageResponse, ProgramResponse,
  QualityResponse, QuadrantResponse,
  RuleResponse, ShortlistResponse, SpendResponse, TargetGroupOption, TargetGroupsResponse,
} from '@/shared/api/types'

/**
 * Programmes — what the campaign delivers to people.
 *
 * ## Four rules govern almost every call in this file
 *
 * **1. A beneficiary's phone and national ID never ride in a URL.** A programme
 * beneficiary list is a list of people who took money from a campaign — the most
 * sensitive table in the product. The lookup is `POST /programs/beneficiaries/lookup`
 * with the value in the BODY, and `?q=` refuses a 7–13 digit string with
 * `program/identifier-in-query` rather than quietly searching names for it. A
 * Kenyan national ID is 7–8 digits, which is why the guard is wider than the
 * mobilizer roster's 9–13.
 *
 * **2. A beneficiary is a PERSON, not a voter.** `voterId` is genuinely nullable
 * and stays that way. Every count that needed a voter link comes back with the
 * number it excluded, and the UI's job is to show that number.
 *
 * **3. Nothing here derives a measurement.** `expectedUpliftPp` is a plan and is
 * labelled Planned everywhere; `uplift` is measured or it is null. A screen that
 * renders 0 where the API sent null is stating that a programme changed nothing,
 * which is a claim nobody made.
 *
 * **4. The rule builder is driven by the backend's own whitelist.** There is no
 * second copy of the segment grammar in this codebase — `targetingFields()` is
 * the source, because PollSphere's builder offered fields its own backend did
 * not evaluate, and that is exactly how a preset comes to match nobody.
 */

// ── programmes ──────────────────────────────────────────────────────────────

export const listPrograms = (params: Record<string, QueryValue>, cursor?: string, limit = 50) =>
  api.get<CursorPage<ProgramResponse>>(`/programs${qs({ ...params, cursor, limit })}`)

export const getProgram = (id: string) =>
  api.get<ProgramResponse>(`/programs/${id}`)

export const createProgram = (body: unknown) =>
  api.post<ProgramResponse>('/programs', body)

export const updateProgram = (id: string, body: unknown) =>
  api.patch<ProgramResponse>(`/programs/${id}`, body)

/**
 * The ONLY way a status changes. Moving to `CANCELLED` additionally requires
 * `program:cancel`, which method security cannot express because the authority
 * depends on the request body — so a 403 from here names the missing authority.
 */
export const changeProgramStatus = (id: string, statusCode: string, reason?: string) =>
  api.post<ProgramResponse>(`/programs/${id}/status`, { statusCode, reason })

// ── targeting ───────────────────────────────────────────────────────────────

export const listRules = (id: string) =>
  api.get<RuleResponse[]>(`/programs/${id}/rules`)

export const publishRule = (id: string, definition: unknown, targetGroupKeys: string[], note?: string) =>
  api.post<RuleResponse>(`/programs/${id}/rules`, { definition, targetGroupKeys, note })

/**
 * A POST because the definition is a JSON tree, not because it mutates anything.
 * Do not "fix" this into a GET with a serialised query string.
 */
export const previewTargeting = (definition: unknown, scopeUnitId?: string | null) =>
  api.post<PreviewResponse>('/programs/targeting/preview', { definition, scopeUnitId })

/** The whitelist itself. The builder renders exactly this and nothing more. */
export const targetingFields = () =>
  api.get<FieldSpec[]>('/programs/targeting/fields')

/**
 * The preset catalogue with a live count AND the tag coverage behind it, so a
 * preset that needs a tag nobody has applied says so instead of showing 0.
 */
export const targetingGroups = (scopeUnitId?: string | null) =>
  api.get<TargetGroupOption[]>(`/programs/targeting/groups${qs({ scopeUnitId })}`)

/** OR across presets, AND with the extra predicate. Two chips widen; they do not narrow. */
export const composeDefinition = (groupKeys: string[], extraDefinition?: unknown) =>
  api.post<Record<string, unknown>>('/programs/targeting/compose', { groupKeys, extraDefinition })

export const getEligibility = (id: string) =>
  api.get<EligibilityResponse>(`/programs/${id}/eligibility`)

export const recomputeEligibility = (id: string) =>
  api.post<EligibilityResponse>(`/programs/${id}/eligibility/recompute`, {})

// ── beneficiaries ───────────────────────────────────────────────────────────

export const listBeneficiaries = (
  programId: string, params: Record<string, QueryValue>, cursor?: string, limit = 50,
) => api.get<CursorPage<BeneficiaryResponse>>(
  `/programs/${programId}/beneficiaries${qs({ ...params, cursor, limit })}`)

export const enrolBeneficiary = (programId: string, body: unknown) =>
  api.post<BeneficiaryResponse>(`/programs/${programId}/beneficiaries`, body)

/**
 * Bulk, from the eligibility snapshot. A snapshot whose pool exceeded the
 * materialisation cap has NO member rows, and this is a 409 naming
 * `materialisedCount` rather than a silent empty success.
 */
export const shortlistBeneficiaries = (programId: string, snapshotId: string, voterIds?: string[]) =>
  api.post<ShortlistResponse>(`/programs/${programId}/beneficiaries/shortlist`, { snapshotId, voterIds })

export const getBeneficiary = (bid: string) =>
  api.get<BeneficiaryResponse>(`/programs/beneficiaries/${bid}`)

export const updateBeneficiary = (bid: string, body: unknown) =>
  api.patch<BeneficiaryResponse>(`/programs/beneficiaries/${bid}`, body)

export const changeBeneficiaryStatus = (
  bid: string, statusCode: string, reasonCode?: string, note?: string, mobilizerId?: string,
) => api.post<BeneficiaryResponse>(`/programs/beneficiaries/${bid}/status`,
  { statusCode, reasonCode, note, mobilizerId })

export const linkBeneficiaryVoter = (bid: string, voterId: string) =>
  api.post<BeneficiaryResponse>(`/programs/beneficiaries/${bid}/link`, { voterId })

/**
 * `fullName` is REQUIRED and that is not an API preference. Unlinking sets
 * `voterId` to null, and the identity constraint then demands a name — which
 * linking cleared. Without one, every unlink is a constraint violation.
 */
export const unlinkBeneficiaryVoter = (
  bid: string, fullName: string, nationalId?: string, phone?: string,
) => api.post<BeneficiaryResponse>(`/programs/beneficiaries/${bid}/unlink`,
  { fullName, nationalId, phone })

export const listBeneficiaryEvents = (bid: string) =>
  api.get<BeneficiaryEventResponse[]>(`/programs/beneficiaries/${bid}/events`)

/**
 * The full phone and national ID. Audited server-side, one row per call, naming
 * the caller and the subject — so this is called on an explicit click, the
 * result is never cached and never enters a list response.
 */
export const revealBeneficiaryContact = (bid: string) =>
  api.get<BeneficiaryContactResponse>(`/programs/beneficiaries/${bid}/contact`)

/**
 * The value goes in the BODY. A `nationalId` body additionally needs
 * `program:read-pii` and writes an audit row — without that split this endpoint
 * would be a national-ID oracle behind a plain read permission.
 */
export const lookupBeneficiary = (body: { nationalId?: string; phone?: string }) =>
  api.post<BeneficiaryResponse[]>('/programs/beneficiaries/lookup', body)

export const beneficiaryDuplicates = (params: Record<string, QueryValue> = {}) =>
  api.get<DuplicateReportResponse>(`/programs/beneficiaries/duplicates${qs(params)}`)

// ── milestones, KPIs, documents, partners ───────────────────────────────────
// Each owns its own endpoints. The programme PATCH touches none of them —
// PollSphere sent the whole milestone array inside the programme save, so every
// edit deleted and recreated every milestone and every id changed.

export const listMilestones = (id: string) =>
  api.get<MilestoneResponse[]>(`/programs/${id}/milestones`)
export const createMilestone = (id: string, body: unknown) =>
  api.post<MilestoneResponse>(`/programs/${id}/milestones`, body)
export const updateMilestone = (id: string, mid: string, body: unknown) =>
  api.patch<MilestoneResponse>(`/programs/${id}/milestones/${mid}`, body)
export const deleteMilestone = (id: string, mid: string) =>
  api.del<void>(`/programs/${id}/milestones/${mid}`)

export const listKpis = (id: string) =>
  api.get<KpiResponse[]>(`/programs/${id}/kpis`)
export const createKpi = (id: string, body: unknown) =>
  api.post<KpiResponse>(`/programs/${id}/kpis`, body)
export const updateKpi = (id: string, kid: string, body: unknown) =>
  api.patch<KpiResponse>(`/programs/${id}/kpis/${kid}`, body)
/** Fills `currentValue` for every DERIVED_* KPI. A PATCH on one is a 409. */
export const recomputeKpis = (id: string) =>
  api.post<KpiResponse[]>(`/programs/${id}/kpis/recompute`, {})

export const listDocuments = (id: string) =>
  api.get<DocumentResponse[]>(`/programs/${id}/documents`)
export const reserveDocument = (id: string, filename: string, contentType: string, sizeBytes: number) =>
  api.post<DocumentReserveResponse>(`/programs/${id}/documents/reserve`, { filename, contentType, sizeBytes })
export const confirmDocument = (id: string, body: unknown) =>
  api.post<DocumentResponse>(`/programs/${id}/documents`, body)
/** A presigned URL with a short expiry, never a raw object-store address. */
export const documentDownloadUrl = (id: string, did: string) =>
  api.get<{ url: string }>(`/programs/${id}/documents/${did}/download`)
export const deleteDocument = (id: string, did: string) =>
  api.del<void>(`/programs/${id}/documents/${did}`)

export const listPartners = (id: string) =>
  api.get<PartnerResponse[]>(`/programs/${id}/partners`)
export const createPartner = (id: string, body: unknown) =>
  api.post<PartnerResponse>(`/programs/${id}/partners`, body)
export const updatePartner = (id: string, pid: string, body: unknown) =>
  api.patch<PartnerResponse>(`/programs/${id}/partners/${pid}`, body)
export const deletePartner = (id: string, pid: string) =>
  api.del<void>(`/programs/${id}/partners/${pid}`)

// ── money ───────────────────────────────────────────────────────────────────
// The ledger is APPEND-ONLY. There is no edit and no delete; a correction is a
// reversal, which is a second row with a negative amount. A reversal reverses
// money, not history — the beneficiary's delivery still happened.

export const recordDisbursement = (id: string, body: unknown) =>
  api.post<DisbursementResponse>(`/programs/${id}/disbursements`, body)

export const listDisbursements = (
  id: string, params: Record<string, QueryValue>, cursor?: string, limit = 50,
) => api.get<CursorPage<DisbursementResponse>>(
  `/programs/${id}/disbursements${qs({ ...params, cursor, limit })}`)

export const reverseDisbursement = (disbursementId: string, reasonCode: string, note?: string) =>
  api.post<DisbursementResponse>(`/programs/disbursements/${disbursementId}/reverse`, { reasonCode, note })

export const getSpend = (id: string) =>
  api.get<SpendResponse>(`/programs/${id}/spend`)

export const reconcileSpend = (id: string) =>
  api.post<SpendResponse>(`/programs/${id}/spend/reconcile`, {})

// ── impact ──────────────────────────────────────────────────────────────────

export const getImpact = (id: string) =>
  api.get<ImpactSummary>(`/programs/${id}/impact`)

export const refreshImpact = (id: string) =>
  api.post<ImpactRefreshResponse>(`/programs/${id}/impact/refresh`, {})

/** Counts, strata and means. Never a member id — they are non-participants. */
export const getControlCohort = (id: string) =>
  api.get<ControlCohortResponse>(`/programs/${id}/control-cohort`)

export const drawControlCohort = (id: string) =>
  api.post<ControlCohortResponse>(`/programs/${id}/control-cohort/draw`, {})

export const clearControlCohort = (id: string) =>
  api.del<ControlCohortResponse>(`/programs/${id}/control-cohort`)

export const getFunnel = (id: string) =>
  api.get<FunnelResponse>(`/programs/${id}/funnel`)

export const snapshotFunnel = (id: string) =>
  api.post<FunnelResponse>(`/programs/${id}/funnel/snapshot`, {})

// ── analytics ───────────────────────────────────────────────────────────────
// One filter object across all twelve screens. A filter that means something
// different on two screens is how two screens come to disagree about the same
// programme.

export type AnalyticsParams = Record<string, QueryValue>

const A = '/programs/analytics'

export const analyticsOverview = (p: AnalyticsParams = {}) =>
  api.get<PortfolioOverviewResponse>(`${A}/overview${qs(p)}`)

export const analyticsTargetGroups = (p: AnalyticsParams = {}) =>
  api.get<TargetGroupsResponse>(`${A}/target-groups${qs(p)}`)

export const analyticsCrossTab = (p: AnalyticsParams = {}) =>
  api.get<CrossTabResponse>(`${A}/target-groups/cross-tab${qs(p)}`)

export const analyticsOverlap = (p: AnalyticsParams = {}) =>
  api.get<OverlapResponseT>(`${A}/overlap${qs(p)}`)

/** Cursor and limit ONLY — this endpoint does not bind the filter bean. */
export const analyticsMultiProgramme = (cursor?: string, limit = 50) =>
  api.get<MultiProgrammePage>(`${A}/overlap/multi-programme${qs({ cursor, limit })}`)

export const analyticsCostVsUplift = (p: AnalyticsParams = {}) =>
  api.get<QuadrantResponse>(`${A}/cost-vs-uplift${qs(p)}`)

export const analyticsEcosystem = (p: AnalyticsParams = {}) =>
  api.get<EcosystemResponse>(`${A}/ecosystem${qs(p)}`)

export const analyticsConcentration = (p: AnalyticsParams = {}) =>
  api.get<ConcentrationResponse>(`${A}/ecosystem/concentration${qs(p)}`)

export const analyticsCoverage = (p: AnalyticsParams = {}) =>
  api.get<ProgramCoverageResponse>(`${A}/coverage${qs(p)}`)

export const analyticsGaps = (p: AnalyticsParams = {}) =>
  api.get<GapsResponse>(`${A}/coverage/gaps${qs(p)}`)

export const analyticsGeography = (p: AnalyticsParams = {}) =>
  api.get<GeographyResponse>(`${A}/geography${qs(p)}`)

export const analyticsMoney = (p: AnalyticsParams = {}) =>
  api.get<MoneyResponse>(`${A}/money${qs(p)}`)

export const analyticsBenford = (p: AnalyticsParams = {}) =>
  api.get<BenfordResponse>(`${A}/money/benford${qs(p)}`)

export const analyticsImpact = (p: AnalyticsParams = {}) =>
  api.get<ImpactResponse>(`${A}/impact${qs(p)}`)

export const analyticsEquity = (p: AnalyticsParams = {}) =>
  api.get<EquityResponse>(`${A}/equity${qs(p)}`)

export const analyticsQuality = (p: AnalyticsParams = {}) =>
  api.get<QualityResponse>(`${A}/quality${qs(p)}`)

export const analyticsFlagged = (p: AnalyticsParams = {}, cursor?: string, limit = 50) =>
  api.get<FlaggedPage>(`${A}/quality/flagged${qs({ ...p, cursor, limit })}`)

/** `program:update`, not `program:analytics` — a read permission must not trigger a recompute. */
// `from` and `to` are `@RequestParam`s. Sent in the body they are discarded and
// the job silently runs its default range, while the result echoes back dates
// that contradict the two pickers the user just filled in.
export const recomputeAnalytics = (from?: string, to?: string) =>
  api.post<AnalyticsRecomputeResult>(`${A}/recompute${qs({ from, to })}`, {})

// ── alerts ──────────────────────────────────────────────────────────────────
// Evaluated by the nightly job and delivered by email. PollSphere's rules fired
// only when somebody opened a drawer, which is a tooltip, not an alert.

export const listAlerts = () => api.get<AlertRuleResponse[]>(`${A}/alerts`)
export const createAlert = (body: unknown) => api.post<AlertRuleResponse>(`${A}/alerts`, body)
export const updateAlert = (id: string, body: unknown) =>
  api.patch<AlertRuleResponse>(`${A}/alerts/${id}`, body)
export const deleteAlert = (id: string) => api.del<void>(`${A}/alerts/${id}`)

// ── export ──────────────────────────────────────────────────────────────────

export const requestExport = (exportKindCode: string, filter: unknown, columns?: string[]) =>
  api.post<ExportJobResponse>(`${A}/export`, { exportKindCode, filter, columns })

export const getExportJob = (jobId: string) =>
  api.get<ExportJobResponse>(`${A}/export/${jobId}`)

// Local alias so the import list above stays alphabetical without a cycle.
type OverlapResponseT = import('@/shared/api/types').OverlapResponse

/**
 * What was filmed at this programme's events.
 *
 * `program` owns the ROUTE and `coverage` owns the DATA — the controller calls
 * `coverage.api.CoverageLookup` and never touches its tables. Requires
 * `coverage:read` on top of `program:read`: somebody who can open a programme
 * does not automatically get to see footage from it, and the port filters by the
 * caller's clearance and scope on top of that.
 */
export const programCoverage = (id: string, limit = 20) =>
  api.get<ProgramCoveragePanelResponse>(`/programs/${id}/coverage${qs({ limit })}`)
